// ПРЕ-ГЕНЕРАЦИЯ PDF — `s987.d1` (возврат к `s442.d1`): файл собирается ЗАРАНЕЕ и держится в памяти
// браузера человека; на сервере ничего не хранится. Строка 67 `3f319875cbb881c89a64cda13df7c7b5`.
//
// ⭐ КЛЮЧ СБОРКИ = ПАСПОРТ + ДАТА ДОКУМЕНТА. Тот же ключ — сборка не повторяется (повторный маунт шага 7,
// повторный Open). Другой ключ (правка этапа меняет паспорт) — прежний запрос обрывается, прежний файл
// выбрасывается, сборка заново. Ответ старого запроса после нового не применяется.
//
// ⭐ КТО ЗОВЁТ `prebuild`: шаг 7 на маунте (точка заморозки — выход с шага 6, последнего экрана перед
// разбором) и на Open — оба только после ответа судьи цитат (`s998.d3`); `/r/{token}` — при открытии
// слепка. Кнопка документа только читает состояние.
//
// Состояния: `idle` (сборки не было) · `building` · `ready` (файл в памяти) · `error`. Нажатие в `error` —
// та же сборка заново («Try again», s986). НЕ БРОСАЕТ: исход сети — состояние, причина — в лог.

import { PDF_FILENAME } from './pdfCopy.js';

export const PDF_PATH = '/api/pdf';

/** Граница одной сборки у клиента. Замер cc-211: сервер 6,6–9,5 с, с загрузкой 8,5 МБ — до 17 с. */
export const PDF_TIMEOUT_MS = 60_000;

/** Сколько живёт ссылка на файл после нажатия: iOS забирает файл не сразу. */
export const URL_TTL_MS = 60_000;

export function createPdfStore({
  endpoint = PDF_PATH, fetchImpl = (...a) => globalThis.fetch(...a), timeoutMs = PDF_TIMEOUT_MS,
  log = (...a) => console.info(...a), env = globalThis,
} = {}) {
  let state = { status: 'idle', blob: null, key: null };
  let body = null;
  let ac = null;
  let seq = 0;
  const subs = new Set();
  const set = (next) => {
    state = { ...state, ...next };
    subs.forEach((fn) => { try { fn(state.status); } catch { /* подписчик — чужое окно, могло закрыться */ } });
  };

  async function run(payload, signal) {
    let timer = null;
    try {
      const resp = await Promise.race([
        fetchImpl(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          ...(signal ? { signal } : {}),
        }),
        new Promise((resolve) => { timer = env.setTimeout(() => resolve(null), timeoutMs); }),
      ]);
      if (!resp) return { reason: `timeout_${timeoutMs}ms` };
      if (!resp.ok) return { reason: `http_${resp.status}` };
      const blob = await resp.blob();
      if (!blob || !blob.size) return { reason: 'empty' };
      return { blob };
    } catch {
      return { reason: signal?.aborted ? 'aborted' : 'network' };
    } finally {
      env.clearTimeout(timer);
    }
  }

  function start(key, payload) {
    ac?.abort();
    ac = typeof env.AbortController === 'function' ? new env.AbortController() : null;
    const id = ++seq;
    body = payload;
    set({ status: 'building', blob: null, key });
    run(payload, ac?.signal).then((r) => {
      if (id !== seq) return; // пришёл ответ уже выброшенной сборки
      if (r.blob) set({ status: 'ready', blob: r.blob });
      else {
        log('[pdf] build failed:', r.reason);
        set({ status: 'error', blob: null });
      }
    });
  }

  return {
    get status() { return state.status; },
    get blob() { return state.blob; },
    /** Собрать PDF этого паспорта, если он ещё не собран (или не собирается). */
    prebuild(passport, generatedAt) {
      if (!passport || !generatedAt) return;
      const key = JSON.stringify([generatedAt, passport]);
      if (key === state.key && state.status !== 'error') return;
      start(key, { passport, generatedAt });
    },
    /** Та же сборка заново — после отказа. */
    retry() {
      if (body) start(state.key, body);
    },
    /**
     * Нажатие кнопки в документе `doc`: готово — файл отдаётся сразу, без ожидания; отказ — сборка заново;
     * идёт сборка — ничего (кнопка и так неактивна).
     */
    save(doc) {
      if (state.status === 'error') return this.retry();
      if (state.status !== 'ready' || !state.blob) return undefined;
      return downloadBlob(doc, state.blob, PDF_FILENAME);
    },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

/**
 * Отдать файл из памяти: `a[download]` на Blob URL в документе кнопки. Ссылка — от окна документа
 * (окно Open живёт дольше вкладки визарда не обязательно, но своё окно точно живо на клике).
 */
export function downloadBlob(doc, blob, filename) {
  const win = doc.defaultView ?? globalThis;
  const url = win.URL.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  doc.body.appendChild(a);
  a.click();
  a.remove();
  win.setTimeout(() => win.URL.revokeObjectURL(url), URL_TTL_MS);
  return url;
}

/** Склад страницы: один на вкладку (шаг 7 и его окна Open; страница `/r/`). */
export const pdfStore = createPdfStore();
